import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {useMediaQuery} from 'react-responsive';
import {compose} from 'redux';
import {get} from '../../../../../../../api/api';
import {uriGetDoctorsSearchQueryv2} from '../../../../../../../api/endpoints';
import {AppFormSubmitButton} from '../../../../../../../components/forms';
import Icon from '../../../../../../../components/icon';
import AppTextInput from '../../../../../../../components/input/app-text-input';
import {Row} from '../../../../../../../components/layout';
import {addToWizard} from '../../../../../../../store/actions';
import * as fonts from '../../../../../../../util/fonts';
import {Icons} from '../../../../../../../util/icons';
import {strings} from '../../../../../../../util/strings';
import ArrowIcon from '../../../../../../medical-network/ArrowIcon';
import SearchIcon from '../../../icons/SearchIcon';
import './../day-time-filter/styles.scss';

export interface Doctor {
  id: number;
  name: string;
  specialties: Specialty[];
  rating: string;
  photo: {
    normal: string;
    small: string;
  };
  professionalDetails: {
    languages: Language[];
  };
}

interface Specialty {
  id: number;
  name: string;
  slug: string;
}

interface Language {
  id: string;
  name: string;
}

interface DoctorFilterProps {
  buStyles: any;
  labels: typeof strings.pt.appointments;
  goBack(value: any): void;
  doctorsFound: Doctor[];
  setDoctorsFound: Function;
  setShowNoDoctorsFound: Function;
  setIsLoading: Function;
  setShowDayTime: Function;
  showDayTime: boolean;
  selectedDoctor: any;
  searchValue: string;
  setSearchValue: Function;
  wizard: any;
  addToWizard: Function;
  speciality: any;
}

function DoctorFilter({
  buStyles,
  labels,
  goBack,
  doctorsFound,
  showDayTime,
  setShowDayTime,
  setDoctorsFound,
  setShowNoDoctorsFound,
  selectedDoctor,
  setIsLoading,
  searchValue,
  setSearchValue,
  wizard,
  addToWizard,
  speciality,
}: DoctorFilterProps) {
  const [enabledButton, setEnabledButton] = useState(!!searchValue);

  const featureName = 'ScheduleAppointments-DoctorFilter';

  const isResponsive = useMediaQuery({maxDeviceWidth: 768});

  useEffect(() => {
    if (wizard?.state?.goBackToSearchDoctor) {
      handleOnClick();
      const state = {
        ...wizard.state,
        goBackToSearchDoctor: false,
      };
      addToWizard({state});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizard]);

  const handleOnClick = () => {
    setShowDayTime(false);
    getDoctorsBySearch();
  };

  const getDoctorsBySearch = async () => {
    setIsLoading(true);
    setShowNoDoctorsFound(false);
    await get(
      uriGetDoctorsSearchQueryv2(searchValue, [speciality.alias]),
      false,
      0,
      featureName,
    )
      .then((res: any) => {
        setDoctorsFound(res?.data?.items);
      })
      .catch(() => {
        setShowNoDoctorsFound(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className="filter-container">
      {!isResponsive && (
        <div className="doctor-filter-title-container">
          <div className="return-label-container">
            <div
              className="return-to-previous-page"
              onClick={() => {
                setSearchValue(null);
                const state = {
                  ...wizard.state,
                  searchValue: null,
                  selectedDoctor: null,
                };
                addToWizard({state});
                goBack(true);
              }}>
              <ArrowIcon classNames="arrow-goback" />
              <a
                className="app-header-bu-logo-opacity"
                style={{
                  ...fonts.SM,
                  ...fonts.SemiBold,
                  color: buStyles.primaryColor,
                }}>
                {labels?.appointments_goback}
              </a>
            </div>
          </div>
          <span style={{...fonts.MM}} className="see-daytime-label">
            {labels?.appointments_see_doctor}
          </span>
          <div className="doctor-filter-search-input-div">
            <AppTextInput
              className="doctor-filter-search-input"
              value={searchValue}
              onChange={(value) => {
                setSearchValue(value);
                setEnabledButton(!!value);
              }}
              placeholder={
                labels?.appointments_scheduleByDoctorSearchPlaceholder
              }
              customInputIcon={
                <SearchIcon className="icon-search app-form-field-error doctor-search-icon" />
              }
            />
            <AppFormSubmitButton
              className="doctor-filter-search-input-button"
              style={{backgroundColor: buStyles.primaryColor}}
              title=""
              disabled={!enabledButton}
              onClick={handleOnClick}>
              <span>{labels?.appointments_scheduleByDoctorSearch}</span>
            </AppFormSubmitButton>
          </div>
        </div>
      )}
      {isResponsive && (
        <>
          <Row className="daytime-filter-title-container-responsive">
            <div
              className="return-to-previous-page-responsive"
              onClick={() => goBack(true)}>
              <ArrowIcon classNames="arrow-goback" />
              <a
                className="app-header-bu-logo-opacity"
                style={{
                  ...fonts.SM,
                  ...fonts.SemiBold,
                  color: buStyles.primaryColor,
                }}>
                {labels?.appointments_goback}
              </a>
            </div>
          </Row>
          <div style={{...fonts.SM}} className="doctor-filter-title">
            {labels?.appointments_see_doctor}
          </div>
          <div className="doctor-filter-search-input-div">
            <AppTextInput
              value={searchValue}
              className="doctor-filter-search-input"
              onChange={(value) => {
                setSearchValue(value);
                setEnabledButton(!!value);
              }}
              placeholder={
                labels?.appointments_scheduleByDoctorSearchPlaceholder
              }
              customInputIcon={
                <Icon
                  className="icon-search app-form-field-error"
                  style={{bottom: '8px'}}
                  name={Icons.SEARCH}
                  color={buStyles.primaryColor}
                  width="27"
                  height="27"
                />
              }
            />
            <AppFormSubmitButton
              className="doctor-filter-search-input-button"
              style={{backgroundColor: buStyles.primaryColor}}
              title=""
              disabled={!enabledButton}
              onClick={handleOnClick}>
              <span>{labels?.appointments_scheduleByDoctorSearch}</span>
            </AppFormSubmitButton>
          </div>
        </>
      )}
    </div>
  );
}

const mapStateToProps = ({strings, globalStyles, wizard}: any) => ({
  labels: strings.value.pt.appointments,
  buStyles: globalStyles.value,
  wizard: wizard.value,
});

const mapDispatchToProps = (dispatch: any) => ({
  addToWizard: (value: any) => dispatch(addToWizard(value)),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps)(DoctorFilter),
);
